import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { X } from 'lucide-react'
import Button from './Button'

/**
 * Modal dialog.
 *
 * Handles the four things a hand-rolled modal almost always gets wrong:
 * focus moves into the dialog on open and returns to the trigger on close,
 * Tab is trapped inside it, Escape closes it, and the page behind does not
 * scroll.
 *
 * Rendered through a portal so it escapes any ancestor with overflow or a
 * transform, both of which would otherwise clip it.
 */

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

export default function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  // Set false for a form with unsaved input: dismissing by mis-clicking the
  // backdrop and losing everything typed is a genuinely bad outcome.
  dismissable = true,
  className,
}) {
  const panelRef = useRef(null)
  const previouslyFocused = useRef(null)

  // Read via a ref inside the effect below rather than depended on directly.
  // onClose is near-always a fresh inline arrow function from the caller
  // (`onClose={() => setX(false)}`), so it gets a new identity on every
  // render of whatever owns this modal — including a render triggered by
  // typing into a field this same dialog contains. Depending on it (or on a
  // useCallback derived from it) would re-run the whole mount effect below
  // on every keystroke: its cleanup returns focus to the trigger, then setup
  // re-focuses the panel, so focus visibly jumps out of the input after each
  // character. Keying the effect on `open` alone — the only thing that
  // should ever re-run it — fixes that.
  const latest = useRef({ dismissable, onClose })
  latest.current = { dismissable, onClose }

  const close = useCallback(() => {
    if (latest.current.dismissable) latest.current.onClose?.()
  }, [])

  useEffect(() => {
    if (!open) return undefined

    previouslyFocused.current = document.activeElement

    // Lock the background. Without this, scrolling inside the dialog falls
    // through to the page once the dialog's own content ends.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Focus the panel itself rather than the first field: announcing the
    // dialog's title first gives context, and dropping straight into an input
    // skips it.
    const focusTimer = setTimeout(() => panelRef.current?.focus(), 0)

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
        return
      }

      if (event.key !== 'Tab') return

      // Trap focus: without this, Tab walks out of the dialog and into the page
      // behind it, which a screen-reader user cannot see has happened.
      const focusable = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )

      if (!focusable?.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousOverflow
      clearTimeout(focusTimer)

      // Return focus to whatever opened the dialog, so a keyboard user is not
      // dumped back at the top of the document.
      previouslyFocused.current?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby={description ? 'modal-description' : undefined}
    >
      {/* Scrim at ~55%: strong enough to isolate the dialog, not so dark that
          the page behind vanishes and the user loses their place. */}
      <div
        className="absolute inset-0 bg-black/55"
        onClick={close}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={clsx(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden bg-[var(--color-surface)]',
          // Bottom sheet on phones, centred dialog on larger screens — the
          // native idiom on each.
          'rounded-t-xl sm:rounded-lg',
          'border border-[var(--color-line)] shadow-2xl',
          SIZES[size] ?? SIZES.md,
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--color-line)] px-5 py-4">
          <div className="min-w-0">
            <h2 id="modal-title" className="text-base font-semibold text-[var(--color-ink-900)]">
              {title}
            </h2>
            {description && (
              <p id="modal-description" className="mt-0.5 text-sm text-[var(--color-ink-500)]">
                {description}
              </p>
            )}
          </div>

          {dismissable && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="-mr-1.5 -mt-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded text-[var(--color-ink-500)] transition-colors hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink-900)]"
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto scroll-slim px-5 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-line)] bg-[var(--color-surface-sunken)] px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Confirmation dialog for destructive or irreversible actions.
 *
 * The confirm button carries the verb ("Deactivate", "Close case") rather than
 * "OK", so someone who skim-reads the title still sees what they are agreeing
 * to on the button they are about to press.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      dismissable={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-[var(--color-ink-700)]">{message}</p>
    </Modal>
  )
}
